import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/core/utils/utils";

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

interface TooltipTriggerProps
  extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger> {
  /**
   * When true, wraps children in a div to avoid DOM nesting issues
   * (e.g., when the child is a button or other interactive element).
   * This prevents the disabled prop from affecting tooltip behavior.
   * The className will be applied to the wrapper div.
   */
  asDiv?: boolean;
}

const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  TooltipTriggerProps
>(({ asDiv, children, className, ...props }, ref) => {
  if (asDiv) {
    const { asChild: _ignored, ...safeProps } = props;
    return (
      <TooltipPrimitive.Trigger ref={ref} asChild {...safeProps}>
        <div className={className}>{children}</div>
      </TooltipPrimitive.Trigger>
    );
  }

  return (
    <TooltipPrimitive.Trigger ref={ref} className={className} {...props}>
      {children}
    </TooltipPrimitive.Trigger>
  );
});
TooltipTrigger.displayName = "TooltipTrigger";

const tooltipContentVariants = cva(
  // No overflow-hidden: it clips the caret arrow. The transform origin is the
  // one Radix computes from the trigger, so the zoom grows out of the trigger
  // rather than out of the tooltip's own centre.
  "z-50 origin-[var(--radix-popper-transform-origin)] rounded-md px-3 py-1.5 text-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
  {
    variants: {
      tone: {
        dark: "bg-bg-dark fill-bg-dark stroke-none text-primary-foreground",
        card: "border border-stroke-tertiary bg-card fill-card stroke-stroke-tertiary text-primary-text shadow-lg",
      },
      maxWidth: {
        sm: "max-w-[200px]",
        md: "max-w-[300px]",
        lg: "max-w-[400px]",
        xl: "max-w-[500px]",
      },
    },
    defaultVariants: {
      tone: "dark",
      maxWidth: "md",
    },
  },
);

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> &
    VariantProps<typeof tooltipContentVariants>
>(
  (
    { className, sideOffset = 4, maxWidth, tone = "dark", children, ...props },
    ref,
  ) => (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        data-tone={tone}
        className={cn(tooltipContentVariants({ tone, maxWidth, className }))}
        {...props}
      >
        {children}
        {/* Custom caret so the arrow sits outside the padding. */}
        <TooltipPrimitive.Arrow asChild width={14} height={6}>
          <svg viewBox="0 0 14 6" className="-translate-y-px overflow-visible">
            <path
              d="M0 0h14L7.81 5.69a1.2 1.2 0 0 1-1.62 0L0 0Z"
              stroke="none"
            />
            <path
              d="M0.54 0.5L6.19 5.69a1.2 1.2 0 0 0 1.62 0L13.46 0.5"
              fill="none"
            />
          </svg>
        </TooltipPrimitive.Arrow>
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  ),
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
